import { useEffect } from 'react';
import classNames from 'classnames';
import { useMachine, useSelector } from '@xstate5/react';
import { Popover, PopoverButton } from '@crazyegginc/hatch';

import { useModal, useAuthContext } from '/src/hooks';
import { ConditionsPopover } from './ConditionsPopover';
import { AudienceEditorModal } from './AudienceEditorModal';
import { ConfiguredAudience } from './ConfiguredAudience';
import { AUDIENCE_LEVELS, AUDIENCE_MODES } from '../constants';
import { ACTIONS, STATES, audienceBuilderMachine } from '../machines/audience-builder';

import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-circle-filled.svg';
import { ReactComponent as UpsellIcon } from '@crazyegginc/hatch/dist/images/icon-crown-filled.svg';

export function MainAudienceSelector({ value = null, onChange = () => {}, disabled }) {
  const modal = useModal();
  const { capabilities } = useAuthContext();
  const [, , actorRef] = useMachine(audienceBuilderMachine, {
    input: {
      definition: value ?? null,
      mode: AUDIENCE_MODES.TARGETING,
    },
  });
  const definition = useSelector(actorRef, (state) => state.context.definition);
  const mode = useSelector(actorRef, (state) => state.context.mode);
  const allSelectedState = useSelector(actorRef, (state) => state.matches(STATES.ALL_VISITORS));
  const showUpsell = capabilities.audiences.allowedTargetingCriteria.level !== AUDIENCE_LEVELS.ALL;

  useEffect(() => {
    actorRef.send({ type: ACTIONS.HYDRATE, definition: value ?? null });
  }, [actorRef, value]);

  function showAudienceEditor() {
    modal.show(<AudienceEditorModal actorRef={actorRef} onChange={onChange} />);
  }

  function onSelectAll() {
    if (disabled) {
      return;
    }

    actorRef.send({ type: ACTIONS.SELECT_ALL });
    onChange(null);
  }

  function onSelectCriteria(criteria) {
    if (disabled) {
      return;
    }

    actorRef.send({ type: ACTIONS.BEGIN_TARGETING, criteria });
    showAudienceEditor();
  }

  if (definition != null) {
    return <ConfiguredAudience actorRef={actorRef} disabled={disabled} onChange={onChange} />;
  }

  return (
    <Popover className="flex w-full space-x-5">
      {({ open }) => {
        const allSelected = allSelectedState && !open;
        const customSelected = !allSelectedState || open;

        return (
          <>
            <button
              type="button"
              onClick={onSelectAll}
              aria-pressed={allSelected}
              className={classNames('flex h-[60px] w-full grow items-center rounded-[5px] px-5 space-x-2.5', {
                'cursor-pointer': !disabled,
                'cursor-not-allowed': disabled,
                'border-lynch-500': disabled && allSelected,
                'border-mystic-500 border': !allSelected,
                'border-dodger-blue-500 border-2': !disabled && allSelected,
              })}
            >
              {allSelected ? (
                <TickIcon
                  className={classNames('h-4 w-4 fill-current', {
                    'text-dodger-blue-500': !disabled,
                    'text-lynch-500': disabled,
                  })}
                />
              ) : (
                <div className="h-4 w-4 rounded-full border border-cadet-blue-500" />
              )}
              <span className={classNames('text-body-1 leading-none')}>All visitors</span>
            </button>
            <div className="relative w-full grow">
              <PopoverButton
                aria-pressed={customSelected}
                className={classNames('flex h-[60px] w-full grow items-center rounded-[5px] px-5 space-x-2.5', {
                  'cursor-pointer': !disabled,
                  'cursor-not-allowed': disabled,
                  'border-lynch-500': disabled && customSelected,
                  'border-mystic-500 border': !customSelected,
                  'border-dodger-blue-500 border-2': !disabled && customSelected,
                })}
              >
                {customSelected ? (
                  <TickIcon
                    className={classNames('h-4 w-4 fill-current', {
                      'text-dodger-blue-500': !disabled,
                      'text-lynch-500': disabled,
                    })}
                  />
                ) : (
                  <div className="h-4 w-4 rounded-full border border-cadet-blue-500" />
                )}
                <span className={classNames('text-body-1 leading-none')}>Targeted visitors</span>
                {showUpsell ? (
                  <UpsellIcon className="text-[#fbbd33] ml-2.5 h-4 w-4 fill-current" aria-label="upsell" />
                ) : null}
              </PopoverButton>
              <ConditionsPopover mode={mode} onClick={onSelectCriteria} />
            </div>
          </>
        );
      }}
    </Popover>
  );
}
